import { runtimeUrls } from './runtimeUrls';

/**
 * API client for account endpoints.
 */

export interface AccountInfo {
  id: string;
  isAnonymous: boolean;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
}

export interface GameInfo {
  gameId: string;
}

export interface LoginResult {
  id: string;
  isAnonymous: boolean;
  email: string;
  displayName: string | null;
}

export interface ApiErrorResponse {
  error: string;
}

/**
 * Custom error class for API responses that include the HTTP status code.
 */
export class ApiError extends Error {
  constructor(
    public message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Safely parse an error response from the API.
 * Falls back to a generic message if the response isn't valid JSON.
 */
async function parseErrorResponse(response: Response, fallbackMessage: string): Promise<string> {
  try {
    const data = (await response.json()) as ApiErrorResponse;
    return data.error || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

/**
 * Get current authenticated user info.
 */
export async function getMe(): Promise<AccountInfo | null> {
  const response = await fetch(runtimeUrls.getAuthUrl('me'), {
    credentials: 'include',
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Failed to get account info');
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<AccountInfo>;
}

/**
 * Login with email and password.
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(runtimeUrls.getAuthUrl('login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Login failed');
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<LoginResult>;
}

/**
 * Claim an anonymous account with email and password.
 */
export async function claimEmail(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(runtimeUrls.getAuthUrl('claim/email'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Failed to claim account');
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<LoginResult>;
}

/**
 * Create a new account with email and password.
 * Used when user doesn't have an anonymous session to claim.
 */
export async function signup(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(runtimeUrls.getAuthUrl('signup'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Signup failed');
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<LoginResult>;
}

/**
 * Logout current session.
 */
export async function logout(): Promise<void> {
  const response = await fetch(runtimeUrls.getAuthUrl('logout'), {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Logout failed');
    throw new ApiError(message, response.status);
  }
}

/**
 * Get list of games the current account has joined.
 */
export async function getGames(): Promise<GameInfo[]> {
  const response = await fetch(runtimeUrls.getAuthUrl('games'), {
    credentials: 'include',
  });

  if (response.status === 401) {
    return [];
  }

  if (!response.ok) {
    const message = await parseErrorResponse(response, 'Failed to get games');
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<GameInfo[]>;
}

/**
 * Get the URL to initiate Google OAuth login.
 * The backend handles whether Google OAuth is configured.
 */
export function getGoogleAuthUrl(): string {
  return runtimeUrls.getAuthUrl('google');
}
